import React, { useEffect, useState } from 'react';
import { CircleAlert, CircleCheck, Info, LucideIcon, TriangleAlert, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

const ICONS: Record<ToastType, { icon: LucideIcon; className: string }> = {
  success: { icon: CircleCheck, className: 'text-emerald-500' },
  error: { icon: CircleAlert, className: 'text-primary-600' },
  warning: { icon: TriangleAlert, className: 'text-amber-500' },
  info: { icon: Info, className: 'text-neutral-400' },
};

export const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onClose(id), 300); // Allow fade out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, onClose]);

  const { icon: Icon, className: iconClass } = ICONS[type] ?? ICONS.info;

  return (
    <div
      data-type={type}
      className={`pointer-events-auto flex max-w-full items-start gap-2.5 bg-white py-2 pl-3 pr-1.5 font-ui text-[13px] shadow-[0_12px_32px_-12px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06] transition-all duration-300 sm:max-w-sm ${
        message ? 'w-full rounded-2xl sm:w-[360px]' : 'w-fit rounded-[20px]'
      } ${isVisible ? 'animate-fade-up' : '-translate-y-1 scale-[0.98] opacity-0'}`}
    >
      <Icon className={`mt-[3px] h-4 w-4 flex-shrink-0 ${iconClass}`} strokeWidth={2} aria-hidden="true" />
      <div className="min-w-0 flex-1 py-[1px]">
        <p className="leading-5 text-ink">{title}</p>
        {message && <p className="mt-0.5 text-[12px] leading-[18px] text-neutral-500">{message}</p>}
      </div>
      <button
        type="button"
        className="-my-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
        onClick={() => {
          setIsVisible(false);
          setTimeout(() => onClose(id), 300);
        }}
        aria-label="Close"
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
};
